import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Badge,
    Box,
    ButtonBase,
    Dialog,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CustomTooltip from "../../common/CustomTooltip";
import { keyframes } from "@mui/system";
import { RootState } from "../../../redux/store";
import {
    AuditJob,
} from "../../../redux/slice/AI/AiAuditSlice";
import { selectIsValueFoundInToken } from "../../../redux/slice/authSlice";
import { CAN_ACCESS_APPLICATION } from "../../../constants/applicationConstants";
import { tokens } from "../../../theme";
import MedicalNoteAuditPopup from "./MedicalNoteAuditPopup";

// Subtle outward-rippling glow that pulses at ~2s. Draws the eye to the
// header button so the user can locate an in-flight audit at a glance,
// without being loud enough to feel like an error/warning indicator.
const pulseRing = keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(0, 167, 111, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(0, 167, 111, 0); }
    100% { box-shadow: 0 0 0 0 rgba(0, 167, 111, 0); }
`;

const MedicalNoteAuditInFlightButton: React.FC = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const canAccessMedicalAudit = useSelector((state: RootState) =>
        selectIsValueFoundInToken(
            "canAccessApplication",
            CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT,
        )(state),
    );

    const auditJobs = useSelector(
        (state: RootState) => state.aiAuditSlice.auditJobs,
    );

    // Only "uploading" + "processing" jobs surface here. Once an audit
    // completes (success/failure), it's removed from this list. When the
    // list empties, the icon disappears from the header entirely.
    const inFlightJobs = useMemo(() => {
        return Object.values(auditJobs).filter((j: AuditJob) => {
            if (!j) return false;
            if (j.fileMetadata?.docType && j.fileMetadata.docType !== "medical notes") {
                return false;
            }
            return j.status === "uploading" || j.status === "processing";
        });
    }, [auditJobs]);

    const [listOpen, setListOpen] = useState(false);
    const [auditPopupOpen, setAuditPopupOpen] = useState(false);

    const mouseDownInsidePillRef = React.useRef(false);

    const openListFromExplicitUserGesture = () => {
        setListOpen(true);
    };

    const handlePillMouseDown = (event: React.MouseEvent<HTMLElement>) => {
        if (!event) return;
        if (event.isTrusted === false) return;
        if (event.button !== 0) return;
        if (typeof event.detail === "number" && event.detail === 0) return;
        const target = event.target as Node | null;
        const currentTarget = event.currentTarget as Node | null;
        if (target && currentTarget && !currentTarget.contains(target)) return;
        mouseDownInsidePillRef.current = true;
    };

    const handlePillMouseUp = (event: React.MouseEvent<HTMLElement>) => {
        const wasDown = mouseDownInsidePillRef.current;
        // Mouseup always consumes the latch, regardless of whether we open.
        mouseDownInsidePillRef.current = false;
        if (!wasDown) return;
        if (!event) return;
        if (event.isTrusted === false) return;
        if (event.button !== 0) return;
        const target = event.target as Node | null;
        const currentTarget = event.currentTarget as Node | null;
        if (target && currentTarget && !currentTarget.contains(target)) return;
        openListFromExplicitUserGesture();
    };

    const handlePillMouseLeave = () => {
        mouseDownInsidePillRef.current = false;
    };

    const handlePillKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event) return;
        if (event.isTrusted === false) return;
        if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
        event.preventDefault();
        openListFromExplicitUserGesture();
    };

    const handleCloseList = () => setListOpen(false);

    const handleViewJob = (_job: AuditJob) => {
        setAuditPopupOpen(true);
    };

    const handleClosePopup = () => {
        setAuditPopupOpen(false);
    };

    if (!canAccessMedicalAudit) return null;
    if (inFlightJobs.length === 0) return null;

    return (
        <>
            <CustomTooltip
                title={`${inFlightJobs.length} Medical Note Audit${inFlightJobs.length === 1 ? "" : "s"} in progress`}
                arrow
            >
                <ButtonBase
                    onMouseDown={handlePillMouseDown}
                    onMouseUp={handlePillMouseUp}
                    onMouseLeave={handlePillMouseLeave}
                    onKeyDown={handlePillKeyDown}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        ml: 1,
                        height: 36,
                        pl: 0.9,
                        pr: { xs: 0.9, md: 1.4 },
                        borderRadius: "999px",
                        border: `1px solid ${colors.primary[300]}`,
                        backgroundColor: theme.palette.mode === "dark"
                            ? "rgba(0, 167, 111, 0.20)"
                            : "rgba(0, 167, 111, 0.10)",
                        animation: `${pulseRing} 2s ease-in-out infinite`,
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                            backgroundColor: theme.palette.mode === "dark"
                                ? "rgba(0, 167, 111, 0.32)"
                                : "rgba(0, 167, 111, 0.18)",
                        },
                    }}
                >
                    <Badge
                        badgeContent={inFlightJobs.length}
                        color="info"
                        overlap="circular"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        sx={{
                            "& .MuiBadge-badge": {
                                backgroundColor: colors.primary[300],
                                color: "#fff",
                                fontSize: "10px",
                                borderRadius: "50%",
                                top: "-2px",
                                right: "-2px",
                            },
                        }}
                    >
                        <CloudSyncIcon
                            sx={{
                                color: colors.primary[300],
                                fontSize: 20,
                                display: "block",
                            }}
                        />
                    </Badge>
               
                    <Typography
                        sx={{
                            color: colors.primary[300],
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            letterSpacing: "0.01em",
                            whiteSpace: "nowrap",
                            lineHeight: 1,
                            display: { xs: "none", md: "inline" },
                        }}
                    >
                        {inFlightJobs.length === 1
                            ? "Audit Processing"
                            : "Audits Processing"}
                    </Typography>
                </ButtonBase>
            </CustomTooltip>

            <Dialog
                open={listOpen}
                onClose={handleCloseList}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        border: `2px solid ${theme.palette.mode === "dark" ? "#464954" : "#e5e9ed"}`,
                        backgroundImage: theme.palette.mode === "dark"
                            ? "linear-gradient(270deg, #131319, #1d2931)"
                            : "none",
                        backgroundColor: theme.palette.mode === "dark" ? undefined : "#ffffff",
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 3,
                        py: 2,
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)"}`,
                    }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            background: `${colors.primary[300]}1F`,
                            border: `1px solid ${colors.primary[300]}55`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CloudSyncIcon sx={{ color: colors.primary[300], fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            fontWeight={700}
                            sx={{
                                fontSize: "1.05rem",
                                color: colors.grey[100],
                                lineHeight: 1.3,
                            }}
                        >
                            Medical Note Audits — In Progress
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                color: colors.grey[400],
                                mt: 0.2,
                            }}
                        >
                            {inFlightJobs.length} audit
                            {inFlightJobs.length === 1 ? "" : "s"} currently being
                            processed. Click the view icon to inspect any of them.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseList} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Body — table-like list */}
                <Box sx={{ px: 3, py: 2 }}>
                    {/* Column headers */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1.4fr 1.6fr 1fr 80px",
                            gap: 2,
                            px: 1.5,
                            py: 1,
                            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)"}`,
                            mb: 0.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                color: colors.primary[300],
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                            }}
                        >
                            Patient Name
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                color: colors.primary[300],
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                            }}
                        >
                            File
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                color: colors.primary[300],
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                            }}
                        >
                            Status
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                color: colors.primary[300],
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                textAlign: "right",
                            }}
                        >
                            Action
                        </Typography>
                    </Box>

                    {/* Rows */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {inFlightJobs.map((job) => {
                            const patientLabel =
                                job.fileMetadata?.patientNameAtStart?.trim()
                                || (job.fileMetadata?.patientIdAtStart
                                    ? `Patient #${job.fileMetadata.patientIdAtStart}`
                                    : "New patient");
                            const fileNameLabel =
                                job.fileMetadata?.fileName || `Document #${job.documentId}`;
                            const statusLabel =
                                job.status === "uploading"
                                    ? "Submitting"
                                    : "Processing";

                            return (
                                <Box
                                    key={job.documentId}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1.4fr 1.6fr 1fr 80px",
                                        gap: 2,
                                        alignItems: "center",
                                        px: 1.5,
                                        py: 1.2,
                                        borderRadius: "8px",
                                        background: theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.03)"
                                            : "rgba(0,0,0,0.03)",
                                        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                                        transition: "background 0.15s ease",
                                        "&:hover": {
                                            background: theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.06)"
                                                : "rgba(0,0,0,0.06)",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "0.875rem",
                                            color: colors.grey[100],
                                            fontWeight: 500,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                        title={patientLabel}
                                    >
                                        {patientLabel}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "0.875rem",
                                            color: colors.grey[200],
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                        title={fileNameLabel}
                                    >
                                        {fileNameLabel}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            color: colors.primary[300],
                                            fontSize: "0.78rem",
                                            fontWeight: 600,
                                        }}
                                    >
                                        <CloudSyncIcon sx={{ fontSize: 14 }} />
                                        {statusLabel}
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Tooltip title="View report" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewJob(job)}
                                                sx={{
                                                    border: `1px solid ${colors.primary[300]}`,
                                                    borderRadius: "6px",
                                                    color: colors.primary[300],
                                                    "&:hover": {
                                                        backgroundColor: `${colors.primary[300]}22`,
                                                    },
                                                }}
                                            >
                                                <VisibilityIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Dialog>

            <MedicalNoteAuditPopup
                open={auditPopupOpen}
                onClose={handleClosePopup}
                initialAuditData={null}
                auditInProgress={true}
                auditCompleted={false}
                uploadedPdfFile={null}
                dialogSx={{ zIndex: (t: any) => t.zIndex.modal + 10 }}
            />
        </>
    );
};

export default MedicalNoteAuditInFlightButton;
